import { useCallback, useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { AnalyticsContext } from "analytics/context";
import { trackEvent } from "analytics/client";
import { OnboardingStep } from "analytics/events";
import {
  endOnboardingJourney,
  getOnboardingJourney,
  OnboardingPath,
  updateOnboardingJourney,
} from "analytics/journey";
import { getAnalyticsBotSource } from "utils/misc";
import { selectWorkspace } from "@shared/slices/common.slice";
import { sharedLib } from "@shared/services/shared.service";
import { BotType, WorkspaceType } from "@shared/types";
import {
  getTradingIntent,
  INTENT_BOT_TYPES,
} from "features/onboarding/guided/intent";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";

export { endOnboardingJourney } from "analytics/journey";
export type { OnboardingPath } from "analytics/journey";

/**
 * Every screen of the onboarding, in funnel order. `Onboarding - Step
 * Viewed` carries one of these (and its position), so one event gives the
 * whole drop-off funnel; each path skips the steps it doesn't use.
 */
export const ONBOARDING_STEPS = [
  "trading_method",
  "trading_mode",
  "exchange_selection",
  "connect_exchange",
  "practice_account",
  "method_walkthrough",
  "public_bots",
  "channel_selection",
  "connect_external_account",
  "bot_review",
  "bot_setup",
  "bot_confirm",
] as const;
export type JourneyStep = (typeof ONBOARDING_STEPS)[number];

type JourneyEvent =
  | "Step Viewed"
  | "Trading Method Selected"
  | "Trading Mode Selected"
  | "Walkthrough Choice"
  | "Walkthrough Interacted"
  | "Demo Exchange Changed"
  | "Practice Account Ready"
  | "Exchange Selected"
  | "Connect Method Changed"
  | "Exchange Connected"
  | "Public Bot Selected"
  | "Channel Tab Changed"
  | "Channel Selected"
  | "Connect Channel Clicked"
  | "External Account Connected"
  | "Bot Confirm Cancelled"
  | "Bot Created"
  | "Back Clicked"
  | "Step Failed"
  | "Completed";

export type WalkthroughChoice =
  | "preview_bot"
  | "browse"
  | "build_own"
  | "create_demo"
  | "continue_live"
  | "use_exchange"
  | "back";

export type OnboardingErrorCode =
  | "practice_account_failed"
  | "accounts_load_failed"
  | "exchange_connection_failed"
  | "bot_creation_failed"
  | "bot_quota_exceeded"
  | "subscription_required"
  | "completion_failed"
  | "skip_failed";

export interface JourneyProperties {
  step?: JourneyStep | "completion" | "skip";
  trading_method?: string;
  trading_mode?: "demo" | "live" | null;
  onboarding_path?: OnboardingPath;
  exchange?: string;
  reused?: boolean;
  bot_type?: string;
  /** Why a step showed: the automatic route, a user's change, a revisit. */
  reason?: "automatic" | "change_exchange" | "revisit";
  choice?: WalkthroughChoice;
  action?: "next_event" | "replay" | "play" | "pause" | "select_event";
  stage?: number;
  connect_method?: "quick_connect" | "api_keys";
  /** Quick connect found the user already connected: no account added. */
  already_connected?: boolean;
  tab?: "trial" | "my" | "discover";
  placement?: "card" | "link" | "empty_state";
  group_id?: number;
  bot_uuid?: string;
  /** Position in the list the user picked from (1 = first). */
  rank?: number;
  category?: string;
  external_platform?: "telegram" | "discord";
  from_step?: JourneyStep;
  error_code?: OnboardingErrorCode;
}

/** A bot type as the onboarding's method name ("signals", "dca", …). */
export const getBotMethod = (botType?: BotType): string | undefined =>
  botType === BotType.TRADING_VIEW_SIGNALS
    ? "tradingview"
    : (Object.keys(INTENT_BOT_TYPES) as (keyof typeof INTENT_BOT_TYPES)[]).find(
        (method) => INTENT_BOT_TYPES[method] === botType
      );

/** Steps before the user has chosen demo or live: no mode yet. */
const BEFORE_MODE = new Set<string>(["trading_method", "trading_mode"]);

/**
 * Tracks one onboarding event. Method, mode and path come from the
 * attempt (set by the user's own choices), so they stay right across
 * redirects and URL rewrites; an event's own properties win.
 */
export const useOnboardingAnalytics = () => {
  const { analytics } = useContext(AnalyticsContext);
  const workspace = useSelector(selectWorkspace);
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const location = useLocation();
  const urlMethod =
    getTradingIntent(new URLSearchParams(location.search)) ||
    (location.pathname.includes("dca-bot")
      ? "dca"
      : location.pathname.includes("signals-group")
      ? "signals"
      : undefined);
  return useCallback(
    (event: JourneyEvent, properties: JourneyProperties = {}) => {
      if (!analytics || !user?.should_track) return;
      const journey = getOnboardingJourney();
      const method =
        properties.trading_method || urlMethod || journey.trading_method;
      const beforeMode =
        event === "Trading Method Selected" ||
        (event === "Step Viewed" && BEFORE_MODE.has(properties.step ?? ""));
      const mode =
        properties.trading_mode !== undefined
          ? properties.trading_mode
          : beforeMode
          ? null
          : journey.trading_mode ??
            (workspace === WorkspaceType.DEMO ? "demo" : "live");
      const stepIndex =
        event === "Step Viewed" && properties.step
          ? ONBOARDING_STEPS.indexOf(properties.step as JourneyStep) + 1
          : undefined;
      trackEvent(`Onboarding - ${event}`, {
        platform: "Website",
        is_telegram_mini_app: IS_TELEGRAM_WEB_APP,
        onboarding_attempt_id: journey.attempt_id,
        trading_method: method ?? null,
        trading_mode: mode,
        onboarding_path:
          properties.onboarding_path ?? journey.onboarding_path ?? null,
        seconds_in_onboarding: Math.round(
          (Date.now() - journey.started_at) / 1000
        ),
        ...(event === "Completed" && method ? { bot_type: method } : {}),
        ...(stepIndex
          ? { step_index: stepIndex, previous_step: journey.last_step ?? null }
          : {}),
        ...properties,
      });
      // The user's choices define the rest of the attempt.
      if (event === "Trading Method Selected" && properties.trading_method)
        updateOnboardingJourney({
          trading_method: properties.trading_method,
          ...(properties.trading_method !== journey.trading_method
            ? { trading_mode: undefined, onboarding_path: undefined }
            : {}),
        });
      else if (event === "Trading Mode Selected")
        updateOnboardingJourney({
          trading_method: method,
          trading_mode: properties.trading_mode ?? undefined,
          onboarding_path: properties.onboarding_path,
        });
      else if (properties.onboarding_path)
        updateOnboardingJourney({
          onboarding_path: properties.onboarding_path,
        });
      if (event === "Step Viewed" && properties.step)
        updateOnboardingJourney({ last_step: properties.step });
      if (event === "Completed") endOnboardingJourney();
    },
    [analytics, user?.should_track, urlMethod, workspace]
  );
};

/**
 * `Onboarding - Step Viewed` once per screen visit: effect replays and
 * re-renders don't repeat it; leaving and coming back does.
 */
export const useOnboardingStepView = (
  step?: JourneyStep,
  properties?: Pick<JourneyProperties, "reason" | "exchange">
): void => {
  const { analytics } = useContext(AnalyticsContext);
  const track = useOnboardingAnalytics();
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const { pathname, search } = useLocation();
  const method = getTradingIntent(new URLSearchParams(search));
  const stepMethod = step === "trading_method" ? "" : method || "";
  const stepKey = `${user?.id}:${pathname}:${step}:${stepMethod}`;
  const lastStep = useRef<string | undefined>(undefined);
  const extra = useRef(properties);
  extra.current = properties;
  useEffect(() => {
    if (!step) {
      lastStep.current = undefined;
      return;
    }
    if (!analytics || !user?.should_track || lastStep.current === stepKey)
      return;
    lastStep.current = stepKey;
    if (step === "trading_method")
      analytics.onboardingTradingMethodViewed({
        onboardingStep: OnboardingStep.TRADING_METHOD,
        botSource: getAnalyticsBotSource(),
      });
    track("Step Viewed", { step, ...extra.current });
  }, [analytics, user?.should_track, step, stepKey, track]);
};
